import { useState, useCallback, useEffect, useMemo, useRef } from "react"

import { isRTableContent, toggleSelection } from "./elementData"
import {
  useElementBase,
  ElementAdapter,
  ElementBaseContext,
} from "./useElementBase"
import { usePieChartConfig } from "./useElementConfigFactory"

import { generateChartImage } from "services/ChartImageService"
import { ScriptGenerationService } from "services/ScriptGenerationService"

import { convertDataToTable } from "utils/convertDataToTable"

import {
  DEFAULT_PIE_CHART_WIDTH,
  DEFAULT_PIE_CHART_HEIGHT,
} from "constants/dashboardElementSizes"

import {
  SlideElement,
  DataElementConfig,
  STATISTIC_TYPE,
  LEGEND_POSITION,
  PieChartElementSettings,
  ELEMENT_TYPE,
} from "types/ReportingDashboard/elements.types"
import { CodebookData } from "types/codebook.types"
import { QueryInstance } from "types/engine.types"
import { EngineOutputItem } from "types/engineOutput.types"
import { ElementRef } from "../../../../features/Reporting/components/ReportingDisplay/ReportingDisplay.component"

/**
 * Element update payload for pie chart elements
 */
interface PieChartElementUpdate {
  width?: number
  height?: number
}

/**
 * Props for usePieChartElement hook
 */
interface UsePieChartElementProps {
  /** Global filter string passed to data elements */
  filter?: string
  /** Element object to render */
  element: SlideElement
  /** Data structure for variable selection */
  codebook: CodebookData
  /** Instance ID for engine queries */
  queryInstance: QueryInstance | null
  /** Callback when element properties change */
  onUpdate: (elementId: string, updates: PieChartElementUpdate) => void
  /** Configuration for pie chart element */
  config?: DataElementConfig | null
  /** Callback for config updates */
  onConfigUpdate: (config: DataElementConfig | null) => void
  /** Whether in dashboard mode */
  isDashboard?: boolean
  /** Callback when element deletion is requested */
  onDeleteRequest?: () => void
  /** Sequential execution request handler */
  onElementExecutionRequest?: (
    elementId: string,
    executeFunction: () => Promise<void>,
    reason?: string
  ) => void
  /** Element registration handler */
  onRegisterElementRef?: (elementId: string, ref: ElementRef | null) => void
}

/**
 * Chart content data structure
 */
interface ChartContent {
  data: Array<{
    statistic: STATISTIC_TYPE
    data: Array<{
      id: string | number | null
      label: string | number | null
      value: number
    }>
  }>
  statisticTypes: STATISTIC_TYPE[]
}

type PieContext = ElementBaseContext<EngineOutputItem>

/**
 * Static part of the pie chart adapter (the submenu-closing callbacks are
 * added per render in the hook body, where closeSubmenus exists).
 *
 * Pinned pie quirks preserved here: the executor validates the variable
 * BEFORE marking an attempted load (leaving isQueuedForExecution stuck true
 * on invalid config), and the internal auto-run conditions omit !showSelects
 * (risk item 3 — harmless, useElementState re-guards internally).
 */
const pieAdapterStatics: Omit<
  ElementAdapter<EngineOutputItem>,
  "onAfterRefresh" | "onAfterEdit" | "onAfterDelete"
> = {
  elementType: ELEMENT_TYPE.PIE_CHART,
  isTimeSeries: false,
  useConfigHook: usePieChartConfig,
  defaultSize: {
    width: DEFAULT_PIE_CHART_WIDTH,
    height: DEFAULT_PIE_CHART_HEIGHT,
  },
  reactsToFilter: true,
  internalAutoRunEnabled: true,
  internalAutoRunConditions: (ctx): boolean => {
    const hasVariable = !!ctx.getValue<string | null>("selectedVariable")
    return hasVariable && !!ctx.queryInstance && !ctx.configHook.isInitializing
  },
  convertsRTableData: true,
  configShowsData: (config): boolean =>
    !!(config as PieChartElementSettings).selectedVariable,
  resetDataForConfigLoad: (ctx: PieContext): void => {
    ctx.setData(null)
  },
  hasRequiredSelections: (ctx): boolean =>
    !!ctx.getValue<string | null>("selectedVariable"),
  autoRunSignature: (ctx): string =>
    String(ctx.getValue<string | null>("selectedVariable")),
  hasDataAndShouldRefresh: (ctx): boolean =>
    !!(ctx.getValue<string | null>("selectedVariable") && ctx.data),
  // Main script execution function (called by the sequential executor)
  execute: async (ctx): Promise<void> => {
    const selectedVariable = ctx.getValue<string | null>("selectedVariable")
    const selectedStatisticType = ctx.getValue<STATISTIC_TYPE>(
      "selectedStatisticType",
      STATISTIC_TYPE.N
    )
    const selectedDashboardCategories = ctx.getValue<string[]>(
      "dashboardSelectedCategories",
      []
    )

    if (!selectedVariable) {
      console.warn("Variable is required for pie chart execution")
      return
    }
    if (ctx.isLoading) {
      console.warn("executePieChartScript aborted: Already running")
      return
    }

    ctx.clearError()
    ctx.markAttemptedLoad()

    try {
      // Generate script using the centralized service
      const script =
        ScriptGenerationService.generatePieChartWithEmptyQTableScript(
          ctx.element.id,
          selectedVariable,
          selectedStatisticType,
          ctx.filter
        )

      const result = await ctx.executeScript(script, ctx.element.id)

      if (result.success) {
        ctx.setData(result.data as EngineOutputItem)
        ctx.updateConfigState({ showSelects: false })

        if (ctx.isDashboard && result.data && "content" in result.data) {
          try {
            if (!isRTableContent(result.data.content)) {
              console.warn("Result data content is not RTableContent format")
              return
            }

            const localConvertedData = convertDataToTable(result.data.content)
            const categoryDataArray =
              localConvertedData.pieChartData?.categoryData || []
            const statGroup = categoryDataArray.find(
              (d) => d.statistic === selectedStatisticType
            )
            if (statGroup && statGroup.data) {
              const categories = statGroup.data
                .filter((item) => item.id !== "Totals")
                .map((item) => String(item.id))

              // Update config with dashboard defaults if not set
              ctx.configHook.updateConfig({
                dashboardSelectedCategories: selectedDashboardCategories.length
                  ? selectedDashboardCategories
                  : categories,
              })
            }
          } catch (err) {
            console.error("Error initializing dashboard selections:", err)
          }
        }
      }
    } catch (e) {
      console.error(
        `Script execution error for element ${ctx.element.id}:`,
        e
      )
    }
  },
  resetState: (ctx): void => {
    ctx.resetElement()
  },
}

/**
 * Custom Hook: usePieChartElement
 *
 * Pie chart element business logic, composed from useElementBase (shared
 * lifecycle: data loading, config wiring, element state, auto-run,
 * filter-change and registration effects, refresh/edit/delete family)
 * plus the pie adapter (script generation, dashboard-category seeding)
 * and pie-only extras: the categories submenu state, the chartContent memo
 * and the container-ref-gated image effect (which is also the only config
 * persistence path — risk item 9 behavior intentionally unchanged).
 *
 * @param props - Pie chart element configuration and callbacks
 * @returns Object containing all state, handlers, and configuration needed by component
 */
export const usePieChartElement = (props: UsePieChartElementProps) => {
  const { isDashboard = true } = props

  // Local state for UI-specific behavior (created before the adapter so the
  // submenu-closing callbacks can capture it)
  const [showCategoriesMenu, setShowCategoriesMenu] = useState<boolean>(false)
  const [isGeneratingImage, setIsGeneratingImage] = useState<boolean>(false)

  // Refs for image generation
  const chartContainerRef = useRef<HTMLDivElement>(null)
  const lastGeneratedConfigRef = useRef<string | null>(null)

  // Helper functions
  const closeSubmenus = useCallback((): void => {
    setShowCategoriesMenu(false)
  }, [])

  const adapter: ElementAdapter<EngineOutputItem> = {
    ...pieAdapterStatics,
    onAfterRefresh: closeSubmenus,
    onAfterEdit: closeSubmenus,
    onAfterDelete: closeSubmenus,
  }

  const base = useElementBase<EngineOutputItem>(props, adapter)

  const {
    isLoading,
    error,
    configHook,
    data: chartData,
    showSelects,
    hasAttemptedLoad,
    isQueuedForExecution,
    convertedData: convertedChartData,
    selectOptions,
    requestSequentialExecution,
    hasDataAndShouldRefresh,
    handleRefresh,
    handleEdit,
    handleDelete,
  } = base

  // Get values from config with fallbacks
  const selectedVariable = configHook.getConfigValue<string | null>(
    "selectedVariable"
  )
  const selectedStatisticType = configHook.getConfigValue<STATISTIC_TYPE>(
    "selectedStatisticType",
    STATISTIC_TYPE.N
  )
  const legendPosition = configHook.getConfigValue<LEGEND_POSITION>(
    "legendPosition",
    LEGEND_POSITION.RIGHT
  )
  const hideArcLabels = configHook.getConfigValue<boolean>(
    "hideArcLabels",
    false
  )
  const selectedDashboardCategories = configHook.getConfigValue<string[]>(
    "dashboardSelectedCategories",
    []
  )

  // Handlers - simplified using config hook
  const handleCategoryToggle = useCallback(
    (cat: string): void => {
      configHook.setConfigValue(
        "dashboardSelectedCategories",
        toggleSelection(selectedDashboardCategories, cat)
      )
    },
    [selectedDashboardCategories, configHook]
  )

  const handleVariableSelect = useCallback(
    (value: string): void => {
      configHook.setConfigValue("selectedVariable", value)
    },
    [configHook]
  )

  const handleStatisticTypeChange = useCallback(
    (value: string): void => {
      configHook.setConfigValue("selectedStatisticType", value)
    },
    [configHook]
  )

  const handleToggleArcLabels = useCallback((): void => {
    configHook.setConfigValue("hideArcLabels", !hideArcLabels)
    closeSubmenus()
  }, [hideArcLabels, closeSubmenus, configHook])

  const handleLegendPositionChange = useCallback(
    (position: string): void => {
      configHook.setConfigValue("legendPosition", position)
      closeSubmenus()
    },
    [closeSubmenus, configHook]
  )

  // Extract complex expression for stable dependency comparison
  const sortedCategoriesString = useMemo((): string => {
    return selectedDashboardCategories.sort().join(",")
  }, [selectedDashboardCategories])

  // Image generation effect
  /**
   * NOTE (pinned): this effect requires chartContainerRef.current, so in a
   * headless render no image is generated — and since config persistence
   * only happens here (risk item 9, intentionally unchanged), the config is
   * never saved either.
   */
  useEffect(() => {
    if (
      isGeneratingImage ||
      !chartData ||
      !chartContainerRef.current ||
      showSelects
    ) {
      return
    }

    const currentConfigString = JSON.stringify({
      selectedVariable,
      selectedStatisticType,
      legendPosition,
      hideArcLabels,
      categories: sortedCategoriesString,
    })

    if (currentConfigString === lastGeneratedConfigRef.current) {
      return
    }

    const timerId = setTimeout(async () => {
      if (!chartContainerRef.current || isGeneratingImage) return

      try {
        setIsGeneratingImage(true)
        configHook.isGeneratingImageRef.current = true

        if (!isRTableContent(chartData.content)) {
          console.warn(
            "Chart data content is not RTableContent format for image generation"
          )
          return
        }

        const chartRenderData = { content: chartData.content }

        const dataUri = await generateChartImage(
          ELEMENT_TYPE.PIE_CHART,
          chartRenderData,
          {
            selectedVariable,
            selectedStatisticType,
            legendPosition,
            hideArcLabels,
            dashboardSelectedCategories: selectedDashboardCategories,
            width: props.element.width || DEFAULT_PIE_CHART_WIDTH,
            height: props.element.height || DEFAULT_PIE_CHART_HEIGHT,
          }
        )

        lastGeneratedConfigRef.current = currentConfigString

        configHook.saveConfig({
          ...(configHook.config as PieChartElementSettings),
          chartDataUri: dataUri,
        })
      } catch (error) {
        console.error("Error generating pie chart image:", error)
        configHook.saveConfig({
          ...(configHook.config as PieChartElementSettings),
          chartDataUri: null,
        })
      } finally {
        setIsGeneratingImage(false)
        setTimeout(() => {
          configHook.isGeneratingImageRef.current = false
        }, 100)
      }
    }, 700)
    return () => clearTimeout(timerId)
  }, [
    chartData,
    showSelects,
    selectedVariable,
    selectedStatisticType,
    legendPosition,
    hideArcLabels,
    sortedCategoriesString,
    isGeneratingImage,
    selectedDashboardCategories,
    props.element.width,
    props.element.height,
    configHook,
  ])

  // Processed chart content for rendering
  const chartContent = useMemo((): ChartContent | null => {
    if (!convertedChartData || !convertedChartData.pieChartData) {
      return null
    }

    try {
      const { categoryData, statisticTypes } = convertedChartData.pieChartData

      if (!categoryData || categoryData.length === 0) {
        return null
      }

      let filteredCategoryData = categoryData

      if (isDashboard && selectedDashboardCategories.length > 0) {
        filteredCategoryData = categoryData
          .map((statGroup) => ({
            ...statGroup,
            data: (statGroup.data || []).filter(
              (item) =>
                selectedDashboardCategories.includes(String(item.id)) ||
                item.id === "Totals" // Always include Totals if present
            ),
          }))
          .filter((statGroup) => statGroup.data && statGroup.data.length > 0)
      }

      if (filteredCategoryData.length === 0) {
        return null
      }

      return {
        data: filteredCategoryData,
        statisticTypes: statisticTypes || [],
      }
    } catch (err) {
      console.error("Error preparing chart data:", err)
      return null
    }
  }, [convertedChartData, isDashboard, selectedDashboardCategories])

  // Return all state, handlers, and configuration needed by the component.
  return {
    // State
    chartData,
    convertedChartData,
    chartContent,
    showSelects,
    isLoading,
    isQueuedForExecution,
    error,
    hasAttemptedLoad,
    showCategoriesMenu,
    setShowCategoriesMenu,

    // Config values
    selectedVariable,
    selectedStatisticType,
    legendPosition,
    hideArcLabels,
    selectedDashboardCategories,

    // Hooks
    configHook,

    // Handlers
    handleRefresh,
    handleEdit,
    handleDelete,
    handleVariableSelect,
    handleStatisticTypeChange,
    handleToggleArcLabels,
    handleLegendPositionChange,
    handleCategoryToggle,
    closeSubmenus,
    requestSequentialExecution,

    // Imperative handle values
    hasDataAndShouldRefresh,

    // Refs and utilities
    chartContainerRef,
    selectOptions,
  }
}
